import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Types
export interface FormInfo {
  form_id: string;
  name: string;
  category: string;
  description?: string;
  score?: number;
}

export interface FieldInfo {
  name: string;
  label: string;
  type: string;
  required: boolean;
  format?: string;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
}

export interface Progress {
  current: number;
  total: number;
  percent: number;
}

export interface ChatResponse {
  session_id: string;
  response: {
    type: string;
    message: string;
    forms?: FormInfo[];
    current_field?: FieldInfo;
    progress?: Progress;
    preview_url?: string;
    download_url?: string;
    next_action?: string;
  };
  state: string;
}

export interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export interface DocumentResult {
  document_id: string;
  session_id: string;
  form_id: string;
  form_name: string;
  filename: string;
  file_size: number;
  download_url: string;
  message: string;
}

// 통합 파이프라인 타입
export interface IntegratedGenerateRequest {
  session_id?: string;
  form_id: string;
  user_data: Record<string, any>;
  case_description?: string;
  use_llm?: boolean;
  verify_hallucination?: boolean;
}

export interface IntegratedGenerateResponse {
  success: boolean;
  document_id?: string;
  form_id: string;
  form_name: string;
  requirements_count: number;
  requirements_fulfilled: number;
  replacements_count: number;
  generated_narrative?: Record<string, string>;
  filename?: string;
  download_url?: string;
  processing_time_ms: number;
  confidence: number;
  warnings: string[];
  errors: string[];
  mode: string;
}

export interface TemplateInfo {
  form_id: string;
  template_path: string;
  exists: boolean;
  size: number;
}

export interface RequirementsInfo {
  form_id: string;
  form_name: string;
  category: string;
  elements: string[];
  legal_basis: string[];
  narrative_structure: string[];
}

// API Functions
export const sendMessage = async (
  message: string,
  sessionId?: string,
  memberType?: 'general' | 'professional'
): Promise<ChatResponse> => {
  const response = await api.post('/chat/message', {
    session_id: sessionId,
    message,
    member_type: memberType || 'general',
  });
  return response.data;
};

export const selectForm = async (
  sessionId: string,
  formId: string
): Promise<ChatResponse> => {
  const response = await api.post('/chat/select-form', {
    session_id: sessionId,
    form_id: formId,
  });
  return response.data;
};

export const submitField = async (
  sessionId: string,
  fieldName: string,
  value: string
): Promise<ChatResponse> => {
  const response = await api.post('/chat/submit-field', {
    session_id: sessionId,
    field_name: fieldName,
    value,
  });
  return response.data;
};

export const searchForms = async (
  query: string,
  category?: string,
  limit: number = 5,
  memberType?: 'general' | 'professional'
): Promise<{ results: FormInfo[]; total: number }> => {
  const params = new URLSearchParams({ q: query, limit: limit.toString() });
  if (category) params.append('category', category);
  if (memberType) params.append('member_type', memberType);

  const response = await api.get(`/search/forms?${params}`);
  return response.data;
};

export const getCategories = async (): Promise<{
  categories: { id: string; name: string; count: number }[];
  total_forms: number;
}> => {
  const response = await api.get('/search/categories');
  return response.data;
};

export const getFormDetail = async (formId: string) => {
  const response = await api.get(`/forms/${formId}`);
  return response.data;
};

export const generatePreview = async (
  sessionId: string,
  formId: string,
  data: Record<string, string>
) => {
  const response = await api.post('/generate/preview', {
    session_id: sessionId,
    form_id: formId,
    data,
  });
  return response.data;
};

export const generateDocument = async (sessionId: string): Promise<DocumentResult> => {
  const response = await api.post('/generate/document-direct', null, {
    params: { session_id: sessionId }
  });
  return response.data;
};

export const generateDocumentFromPreview = async (
  previewId: string,
  format: string = 'docx'
) => {
  const response = await api.post('/generate/document', {
    preview_id: previewId,
    format,
  });
  return response.data;
};

export const getSessionInfo = async (sessionId: string) => {
  const response = await api.get(`/chat/session/${sessionId}`);
  return response.data;
};

export const generateAIFields = async (sessionId: string) => {
  const response = await api.post('/chat/generate-ai-fields', null, {
    params: { session_id: sessionId }
  });
  return response.data;
};

// ============================================================================
// 통합 파이프라인 API (NEW - HWP 문서 생성)
// ============================================================================

/**
 * 통합 파이프라인으로 HWP 문서 생성
 * RAG → 요건사실 검증 → LLM 서술 생성 → 환각 탐지 → HWP 교체
 */
export const generateIntegratedDocument = async (
  request: IntegratedGenerateRequest
): Promise<IntegratedGenerateResponse> => {
  const response = await api.post('/integrated/generate', request);
  return response.data;
};

/**
 * 생성된 HWP 문서 다운로드 URL 반환
 */
export const getIntegratedDownloadUrl = (documentId: string): string => {
  return `${API_BASE_URL}/integrated/download/${documentId}`;
};

/**
 * 필드 값 제출 (통합 파이프라인용)
 */
export const submitIntegratedField = async (
  sessionId: string,
  fieldName: string,
  value: string
): Promise<{ success: boolean; progress: Progress }> => {
  const response = await api.post('/integrated/submit-field', {
    session_id: sessionId,
    field_name: fieldName,
    value,
  });
  return response.data;
};

/**
 * 사용 가능한 HWP 템플릿 목록 조회
 */
export const getAvailableTemplates = async (): Promise<{
  templates: TemplateInfo[];
  total: number;
  available: number;
}> => {
  const response = await api.get('/integrated/templates');
  return response.data;
};

/**
 * 서식의 요건사실 조회
 */
export const getFormRequirements = async (formId: string): Promise<RequirementsInfo> => {
  const response = await api.get(`/integrated/requirements/${formId}`);
  return response.data;
};

/**
 * HWP 문서 생성 (간편 버전)
 * - use_llm=true: LLM으로 고품질 서술 생성 (API 크레딧 사용)
 * - use_llm=false: Fallback 모드 (빠르지만 품질 낮음)
 */
export const generateHwpDocument = async (
  formId: string,
  userData: Record<string, any>,
  caseDescription?: string,
  useLlm: boolean = true,
  sessionId?: string
): Promise<IntegratedGenerateResponse> => {
  return generateIntegratedDocument({
    session_id: sessionId,
    form_id: formId,
    user_data: userData,
    case_description: caseDescription,
    use_llm: useLlm,
    verify_hallucination: useLlm,
  });
};

// ============================================================================
// Canvas UI용 API (NEW - GPT Canvas 스타일)
// ============================================================================

/**
 * Canvas UI용 서식 템플릿 응답 타입
 */
export interface FormTemplateResponse {
  form_id: string;
  form_name: string;
  category: string;
  content_html: string;
  editable_fields: string[];
  raw_text?: string;
}

/**
 * Canvas UI용 템플릿 목록 아이템
 */
export interface TemplateListItem {
  form_id: string;
  name: string;
  category: string;
}

/**
 * AI 부분 수정 요청
 */
export interface AIEditRequest {
  selection: string;
  instruction: string;
  full_content?: string;
}

/**
 * AI 부분 수정 응답
 */
export interface AIEditResponse {
  suggested_text: string;
  confidence: number;
}

/**
 * Canvas UI용: 서식 템플릿 목록 조회
 */
export const getFormTemplates = async (
  category?: string
): Promise<TemplateListItem[]> => {
  const params = new URLSearchParams();
  if (category) params.append('category', category);
  const response = await api.get(`/forms/templates?${params}`);
  return response.data;
};

/**
 * Canvas UI용: 서식 템플릿 HTML 조회
 * - HWP를 HTML로 변환하여 반환
 * - TipTap 에디터에 바로 표시 가능
 * - formName: DB form_id와 Registry form_id 불일치 시 이름으로 fallback 검색
 */
export const getFormTemplate = async (
  formId: string,
  formName?: string
): Promise<FormTemplateResponse> => {
  const params = new URLSearchParams();
  if (formName) params.append('name', formName);
  const query = params.toString();
  const url = `/forms/templates/${formId}${query ? '?' + query : ''}`;
  const response = await api.get(url);
  return response.data;
};

/**
 * Canvas UI용: AI 부분 수정 요청
 * - 선택한 텍스트를 AI가 지시에 따라 수정
 */
export const aiEditForm = async (
  formId: string,
  request: AIEditRequest
): Promise<AIEditResponse> => {
  const response = await api.post(`/forms/templates/${formId}/ai-edit`, request);
  return response.data;
};

/**
 * Canvas UI용: 편집된 HTML → HWP 생성
 * - Canvas에서 편집한 내용을 HWP 파일로 변환
 */
export const generateCanvasHwp = async (
  formId: string,
  contentHtml: string
): Promise<{ success: boolean; download_url: string; filename: string }> => {
  const response = await api.post(`/forms/templates/${formId}/canvas-hwp`, {
    content_html: contentHtml
  });
  return response.data;
};

export default api;
